import React, { useState, useEffect, useRef, useMemo } from 'react';

import { AlmeAppState, AlmeSavedAppState, createChannelTypes } from './AlmeAppState';
import { AlmeApiUtils } from '../../api/AlmeApiUtils';
import { createLiveChatNotificationApi, createAlmeApi } from '../../api/AlmeApi';

import { AlmeConfig } from '../../models/AlmeConfig';

import AlmeHeader from '../AlmeHeader/AlmeHeader';
import AlmeHistory from '../AlmeHistory/AlmeHistory';
import AlmeFooter from '../AlmeFooter/AlmeFooter';
import { OnDisplayLinkClick } from '../AlmeResponse/AlmeResponse';
import AlmeLaunchPoint from '../AlmeLaunchPoint/AlmeLaunchPoint';
import { OnMinimizeClick } from '../AlmeMinimize/AlmeMinimize';

import { WithStyles, withStyles } from '@material-ui/core/styles';
import { Container, Grow } from '@material-ui/core';

import { CookieLoader } from '@alme/storage-ts/CookieLoader';

import appStyles from './AlmeApp.styles';
import { AlmeIvaTypeProp } from '../../models/AlmeIvaTypeMapping';
import { OnSurveySubmitClick } from '../ResponseActions/AlmeSurvey/AlmeSurvey';
import { SetIsDialogOpen } from '../AlmeDialog/AlmeDialog';
import { OnCtaClickCallback } from '../ResponseActions/AlmeCtaButton/AlmeCtaButton';


import { ClientCommunicator } from '@alme/post-message-api/ClientCommunicator';
import AlmeLiveChatControls, { OnEndChat } from '../LiveChat/AlmeLiveChatControls/AlmeLiveChatControls';
import { OnSendTypingEvent, OnSubmitAlmeInput } from '../AlmeInput/AlmeInput';
import { useLaunchIvaCallback } from '../../hooks/useLaunchIvaCallback';
import { useResetIvaCallback } from '../../hooks/useResetIvaCallback';
import { UseLiveChatConnected } from '../../hooks/useLiveChatConnected';

import defaultLogger from '../../Logger';

import { CookieAlmeSessionDataLoader, almeSessionCookieName } from '@alme/session-ts/CookieAlmeSessionDataLoader';
import { AlmeSessionData } from '@alme/session-ts/AlmeSessionData';
import { loadAlmeSessionData, useAlmeSessionDataCallback, useClearSessionData, useSaveAlmeSessionData, useSaveLastActivityTime } from '../../hooks/useAlmeSessionData';
import { loadAlmeHistory, processLastResponse } from './AlmeAppUtils';
import { useAlmeInitialization } from '../../hooks/useAlmeInitialization';
import { useSetDefaultLoggingLevel } from '../../hooks/useSetDefaultLoggingLevel';
import { useAlmeMessageListener } from '../../hooks/useAlmeMessageListener';
import { useSyncLastEntry } from '../../hooks/useSyncLastEntry';
import { usePollForNotifications } from '../../hooks/usePollForNotifications';
import { usePollForChat } from '../../hooks/usePollForChat';
import { useInlineIvaStyles } from '../../hooks/useInlineIvaStyles';
import { LiveChatNotificationApiSettings } from '@alme/api-ts/LiveChat/LiveChatNotificationApiSettings';
import { AlmeSessionUtils } from '../../api/AlmeSessionUtils';
import { useSetAlmeStateCallback } from '../../hooks/useSetAlmeStateCallback';
import { ChangeTenantAppEvent } from '../ResponseActions/ChangeTenantConfirmation/ChangeTenantConfirmation';

const sessionLoader = new CookieLoader<AlmeSessionData>(almeSessionCookieName);
const sessionDataLoader = new CookieAlmeSessionDataLoader(sessionLoader);
const savedAppState = new CookieLoader<AlmeSavedAppState>('almeUiState');
const clientCommunicator = new ClientCommunicator();

function getCurrentAppState(): AlmeSavedAppState | null {
    let currentAppState: AlmeSavedAppState | null = null;
    try {
        currentAppState = savedAppState.GetStorage();
    } catch (ex) {
        defaultLogger.error(ex);
    }
    return currentAppState;
}

function CheckForPageNavigation(apiUtils: React.MutableRefObject<AlmeApiUtils>) {
    setTimeout(function () {
        const changeTenantAppEventCookie = new CookieLoader<ChangeTenantAppEvent>('changeTenantAppEvent');
        const oldAccessKeyCookie = new CookieLoader<string>('oldAccessKey');
        const verintAccessKeyCookie = new CookieLoader<string>('verintAccessKey');
        const switchTenantCookie = new CookieLoader<boolean>('switchTenant');
        const oldAccessKey = oldAccessKeyCookie.GetStorage();
        const newAccessKey = verintAccessKeyCookie.GetStorage();

        if (oldAccessKey && oldAccessKey !== '' && switchTenantCookie.GetStorage()) {
            oldAccessKeyCookie.UpdateStorage('');
            switchTenantCookie.UpdateStorage(false);
            const appEvent = {
                Name: "ChangeTenantURL",
                parameters: {
                    NewClientAccessKey: newAccessKey,
                    OldClientAccessKey: oldAccessKey,
                    Initiator: "Conversation",
                },
            } as ChangeTenantAppEvent;

            changeTenantAppEventCookie.UpdateStorage(appEvent);
            // eslint-disable-next-line
            apiUtils.current.submitAppEvent('ChangeTenantURL', 'Internal', appEvent.parameters);
        }
    }, 1000);
}

interface AlmeAppProps extends WithStyles<typeof appStyles> {
    ivaType?: AlmeIvaTypeProp;
    almeConfig: AlmeConfig;
    verintAccessKey?: React.MutableRefObject<string>;
    apiSettings?: LiveChatNotificationApiSettings;
    personaMapped: React.MutableRefObject<boolean>;
    isIvaProLaunch?: React.MutableRefObject<boolean>;
    clearAppData: () => void;
}

const AlmeApp: React.FC<AlmeAppProps> = (props: AlmeAppProps) => {
    const currentAppState = getCurrentAppState();
    const initialAlmeState: AlmeAppState = {
        history: [],
        isOpen: currentAppState?.isOpen || props.ivaType === 'popup',
        isMinimized: currentAppState?.isMinimized || false,
        liveChatIndex: -1,
        isLiveChatConnected: false,
        agentChannel: createChannelTypes(),
    };
    const [almeState, setAlmeState] = useState<AlmeAppState>(initialAlmeState);
    const scrollToResponse = useRef<boolean>(false);
    const [pulse, setPulse] = useState<boolean>(false);
    const isInitialized = useRef<boolean>(false);
    const [almeConfig, setAlmeConfig] = useState<AlmeConfig>(props.almeConfig);
    const [almeSessionData, setAlmeSessionData] = useState<AlmeSessionData>(loadAlmeSessionData(sessionDataLoader));
    const sessionUtils = useRef<AlmeSessionUtils>();
    const [shouldDisplayTimeout, setShouldDisplayTimeout] = useState<boolean>(false);
    // Prevents iva from opening with default skinning on navigation
    useEffect(() => {
        if (almeState.isOpen) {
            setAlmeStateWrapper({
                ...almeState,
                isOpen: false,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setAlmeStateWrapper = useSetAlmeStateCallback(setAlmeState, savedAppState);
    const getAlmeSessionFromStorage = () => loadAlmeSessionData(sessionDataLoader);

    useMemo(() => {
        setAlmeConfig(props.almeConfig);
    }, [props.almeConfig]);

    const onUpdateAlmeSessionData = useAlmeSessionDataCallback(almeSessionData, setAlmeSessionData);
    const clearSessionData = useClearSessionData(sessionDataLoader, savedAppState, setAlmeSessionData, props.clearAppData);
    const clearSessionWithoutTenant = useClearSessionData(sessionDataLoader, savedAppState, setAlmeSessionData, props.clearAppData);
    const saveActivityTIme = useSaveLastActivityTime(almeSessionData, setAlmeSessionData);

    useEffect(() => {
        sessionUtils.current = new AlmeSessionUtils(almeSessionData, almeState, getAlmeSessionFromStorage,
            clearSessionWithoutTenant, setAlmeStateWrapper, saveActivityTIme, sessionUtils.current?.sessionTimer);

    }, [almeSessionData, almeState, clearSessionWithoutTenant, setAlmeStateWrapper, saveActivityTIme]);

    const almeApi = createAlmeApi(props.apiSettings);
    const liveChatNotificationApi = createLiveChatNotificationApi(props.apiSettings);

    const apiUtils = useRef<AlmeApiUtils>(new AlmeApiUtils(almeState, setAlmeStateWrapper, almeSessionData, sessionUtils.current, onUpdateAlmeSessionData, almeConfig, almeApi, liveChatNotificationApi, props.verintAccessKey?.current));
    useEffect(() => {
        const apiConfig = {
            apiUtils: almeConfig.apiUtils,
        };

        apiUtils.current = new AlmeApiUtils(almeState, setAlmeStateWrapper, almeSessionData, sessionUtils.current,
            onUpdateAlmeSessionData, apiConfig, almeApi, liveChatNotificationApi, props.verintAccessKey?.current);
    }, [almeState, almeConfig.apiUtils, setAlmeStateWrapper, almeSessionData, onUpdateAlmeSessionData, props.verintAccessKey, almeApi, liveChatNotificationApi]);

    useSetDefaultLoggingLevel();
    useSaveAlmeSessionData(almeSessionData, sessionDataLoader);
    UseLiveChatConnected(almeState.isLiveChatConnected, almeConfig, setAlmeConfig);
    useAlmeInitialization(apiUtils, almeState, isInitialized, setAlmeStateWrapper, props.personaMapped.current, scrollToResponse, props.almeConfig.history.shouldScrollOnNav, setShouldDisplayTimeout, props.isIvaProLaunch, sessionUtils);
    useInlineIvaStyles(almeState.isOpen, props.ivaType);

    const onDisplayLinkClick = OnDisplayLinkClick(apiUtils, scrollToResponse);
    const onSubmitAlmeInput = OnSubmitAlmeInput(apiUtils, scrollToResponse);
    const onMinimizeClick = OnMinimizeClick(almeState, setAlmeStateWrapper, apiUtils);
    const setIsDialogOpen = SetIsDialogOpen(almeState, setAlmeStateWrapper);
    const onSurveySubmitClick = OnSurveySubmitClick(apiUtils);
    const onCtaClick = OnCtaClickCallback(apiUtils);
    const onEndChat = OnEndChat(apiUtils);
    const onSendTypingEvent = OnSendTypingEvent(apiUtils);
    const onResetIva = useResetIvaCallback(almeState, apiUtils, setAlmeState, clearSessionData, isInitialized, props.personaMapped, props.verintAccessKey);
    const onLaunchIva = useLaunchIvaCallback(almeState, isInitialized, setAlmeStateWrapper, apiUtils, setPulse, scrollToResponse, props.isIvaProLaunch);
    void CheckForPageNavigation(apiUtils);

    useAlmeMessageListener(clientCommunicator, onLaunchIva);
    usePollForNotifications(almeConfig, isInitialized, almeState, apiUtils);
    usePollForChat(almeConfig, isInitialized, almeState, apiUtils);
    useSyncLastEntry(almeState, apiUtils, almeConfig, setAlmeStateWrapper, getCurrentAppState, loadAlmeHistory);

    useEffect(() => {
        if (almeState.isMinimized)
            sessionUtils.current?.setOnSessionTimeout();
    }, [almeState.isMinimized]);

    useEffect(() => {
        if (window.top)
            window.top.postMessage({ ivaAvailability: true }, "*");
    }, []);

    const hasHistory: boolean = almeState.history.length > 0;
    const tabIndex = almeState.isDialogOpen ? -1 : 0;
    const disablePulse = () => {
        if (pulse) {
            setPulse(false);
        }
    };

    const lastResponse = processLastResponse(almeState, setAlmeStateWrapper);

    return (
        <>
            <Grow style={{ transformOrigin: '100% 100% 0' }} in={almeState.isOpen} unmountOnExit>
                <Container disableGutters className={`${props.classes.root} ${props.classes.rootIvaType} ${pulse ? props.classes.rootPulse : ''}`} id='nit-alme-window-root'
                    onPointerEnter={disablePulse} onMouseEnter={disablePulse} onFocus={disablePulse} onTouchStart={disablePulse}
                >
                    <AlmeHeader
                        onMinimizeClick={onMinimizeClick}
                        onUtilityLinkClick={onMinimizeClick}
                        onResetIva={onResetIva}
                        almeConfig={almeConfig}
                        setIsDialogOpen={setIsDialogOpen}
                        focusProps={{ disabled: almeState.isDialogOpen }}
                        isDialogOpen={almeState.isDialogOpen}
                        isLiveChatConnected={almeState.isLiveChatConnected}
                        color={almeConfig.header.styles.backgroundColor}
                        ivaType={props.ivaType}
                    />
                    <AlmeHistory
                        history={almeState.history}
                        almeConfig={almeConfig}
                        focusProps={{ tabIndex, disabled: almeState.isDialogOpen }}
                        isLiveChatConnected={almeState.isLiveChatConnected}
                        setIsDialogOpen={setIsDialogOpen}
                        isDialogOpen={almeState.isDialogOpen}
                        onDisplayLinkClick={onDisplayLinkClick}
                        onSurveySubmitClick={onSurveySubmitClick}
                        onCtaClick={onCtaClick}
                        setShouldDisplayTimeout={setShouldDisplayTimeout}
                        shouldDisplayTimeout={shouldDisplayTimeout}
                        scrollToResponse={scrollToResponse}
                    />
                    <AlmeLiveChatControls
                        responseId={almeState.history.length}
                        almeConfig={almeConfig}
                        isLiveChatConnected={almeState.isLiveChatConnected}
                        response={lastResponse}
                        focusProps={{ disabled: almeState.isDialogOpen }}
                        setIsDialogOpen={setIsDialogOpen}
                        onEndChat={onEndChat}
                        isDialogOpen={almeState.isDialogOpen}
                    />
                    <AlmeFooter
                        isLiveChatConnected={almeState.isLiveChatConnected}
                        onSendTypingEvent={onSendTypingEvent}
                        submitFunc={onSubmitAlmeInput}
                        almeConfig={almeConfig}
                        focusProps={{ disabled: almeState.isDialogOpen }}
                    />
                </Container>
            </Grow>
            <AlmeLaunchPoint
                hasHistory={hasHistory}
                isOpen={almeState.isOpen}
                almeConfig={almeConfig}
                ivaType={props.ivaType}
                color={almeConfig.launchPoint.launchButton.color}
            />
        </>
    );
};

export default withStyles(appStyles)(AlmeApp);
